import React, { useState } from 'react';
import {
  Typography,
  Paper,
  Box,
  Divider,
  Stack,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import { BugReport, Security, DevicesOther } from '@mui/icons-material';
import { DebugButton } from '../../../shared/components/dev/DebugButton';
import { FeatureSwitchGate, Features } from '@billda/features-ui';
import { PermissionGate, Permissions } from '@billda/permissions-ui';
import { useAuthStore } from '../../../features/auth/stores/authStore';
import { useNavigate } from 'react-router-dom';
import { Routes as AppRoutes } from '../../../routes';

/**
 * Security settings area: sign out all devices and developer tools.
 */
export const SecurityArea: React.FC = () => {
  const { logoutAll } = useAuthStore();
  const navigate = useNavigate();
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);

  const handleLogoutAllDevices = () => {
    setConfirmDialogOpen(true);
  };

  const handleConfirmLogoutAll = () => {
    setConfirmDialogOpen(false);
    console.log('👤 User initiated logout all from Settings page');
    navigate(AppRoutes.login);
    logoutAll('manual_user_action_settings');
  };

  const handleCancelLogoutAll = () => {
    setConfirmDialogOpen(false);
  };

  return (
    <>
      {/* Security Section */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Security sx={{ mr: 1, color: 'primary.main' }} />
          <Typography variant="h6">Security</Typography>
        </Box>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Manage your account security settings.
        </Typography>

        <Divider sx={{ mb: 3 }} />

        <Stack spacing={3}>
          <Box>
            <Typography variant="body1" fontWeight={500} gutterBottom>
              Sign Out All Devices
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Sign out from all browsers and devices. You will need to sign in
              again on each device.
            </Typography>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DevicesOther />}
              onClick={handleLogoutAllDevices}
              data-testid="settings-logout-all-button"
            >
              Sign out all devices
            </Button>
          </Box>
        </Stack>
      </Paper>

      {/* Confirm Logout All Dialog */}
      <Dialog
        open={confirmDialogOpen}
        onClose={handleCancelLogoutAll}
        data-testid="confirm-logout-all-dialog"
      >
        <DialogTitle>Sign Out All Devices</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will sign you out from all browsers and devices, including this
            one. You will need to sign in again.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelLogoutAll}>Cancel</Button>
          <Button
            onClick={handleConfirmLogoutAll}
            color="error"
            variant="contained"
            data-testid="confirm-logout-all-button"
          >
            Sign Out All Devices
          </Button>
        </DialogActions>
      </Dialog>

      {/* Developer Tools (permission + feature-gated) */}
      <PermissionGate
        permission={Permissions.Developer.Access}
        showRequiredPermissions={false}
      >
        <FeatureSwitchGate feature={Features.Debug.IsPanelEnabled}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <BugReport sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">Developer Tools</Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Tools and utilities for debugging and development.
            </Typography>

            <Divider sx={{ mb: 3 }} />

            <Stack spacing={3}>
              <Box>
                <Typography variant="body1" fontWeight={500} gutterBottom>
                  Debug Panel
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Open the debug panel to inspect application state, JWT tokens,
                  and user data.
                </Typography>
                <DebugButton size="medium" />
              </Box>
            </Stack>
          </Paper>
        </FeatureSwitchGate>
      </PermissionGate>
    </>
  );
};
